import React from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import KeywordForm from 'talent_search/components/keyword_form';
import PopoverButton from 'talent_matching/components/filters/popover_button';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { withLocalePrefix } from 'shared/utils/translation';
import { Label } from '@grnhse/seedling/lib/birch/typography';
import { HelpTooltip } from '@grnhse/seedling/lib/birch/components/tooltips';

const t = withLocalePrefix('talent_matching.filter_row.keywords');

type FilterType = 'canHave' | 'mustHave' | 'doesNotHave';

const Keywords = () => {
  const { watch, setValue } = useFormContext();
  const [canHave, mustHave, doesNotHave] = watch([
    'canHave',
    'mustHave',
    'doesNotHave',
  ]);

  const filtersApplied = [
    canHave.length,
    mustHave.length,
    doesNotHave.length,
  ].filter(Boolean).length;

  const buttonLabel = t('button', { count: filtersApplied });

  const clear = () => {
    setValue('canHave', []);
    setValue('mustHave', []);
    setValue('doesNotHave', []);
  };

  const handleAdd = (type: FilterType, keywords: string[]) => {
    setValue(type, keywords);
  };

  const handleRemove = (type: FilterType, keyword: string) => {
    setValue(
      type,
      (watch(type) as string[]).filter((k: string) => k !== keyword)
    );
  };

  return (
    <PopoverButton buttonLabel={buttonLabel} title={t('title')} width="500px">
      <Stack spacing={2} direction="column">
        <Stack spacing={1} direction="row">
          <Label>{t('can_have')}</Label>
          <HelpTooltip
            label={t('can_have_description')}
            ariaLabel={t('can_have_description')}
          />
        </Stack>

        <KeywordForm
          filterType="can_have"
          values={canHave}
          onAdd={(keywords) => handleAdd('canHave', keywords)}
          onRemove={(keyword) => handleRemove('canHave', keyword)}
          booleanIndicator={t('or')}
          pageName="search"
        />

        <Stack spacing={1} direction="row">
          <Label>{t('must_have')}</Label>
          <HelpTooltip
            label={t('must_have_description')}
            ariaLabel={t('must_have_description')}
          />
        </Stack>

        <KeywordForm
          filterType="must_have"
          values={mustHave}
          onAdd={(keywords) => handleAdd('mustHave', keywords)}
          onRemove={(keyword) => handleRemove('mustHave', keyword)}
          booleanIndicator={t('and')}
          pageName="search"
        />

        <Stack spacing={1} direction="row">
          <Label>{t('does_not_have')}</Label>
          <HelpTooltip
            label={t('does_not_have_description')}
            ariaLabel={t('does_not_have_description')}
          />
        </Stack>

        <KeywordForm
          filterType="does_not_have"
          values={doesNotHave}
          onAdd={(keywords) => handleAdd('doesNotHave', keywords)}
          onRemove={(keyword) => handleRemove('doesNotHave', keyword)}
          booleanIndicator={t('and')}
          pageName="search"
        />
        <Stack sx={{ alignSelf: 'flex-start' }}>
          <TertiaryButton onClick={clear}>{t('clear')}</TertiaryButton>
        </Stack>
      </Stack>
    </PopoverButton>
  );
};

export default Keywords;
